import { ChevronRight, Clock3, Plus, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";

const chartColors = ["#0B7A75", "#C8A14A", "#4E8D88", "#A98024", "#B7D3CF"];

function MetricCard({ metric }) {
  return (
    <article
      className={`metric-card metric-${metric.tone}`}
      data-testid={`kpi-${metric.label.toLowerCase().replaceAll(" ", "-").replaceAll(".", "")}`}
    >
      <p>{metric.label}</p>
      <div>
        <strong>{metric.value}</strong>
        <span className="trend trend-neutral">{metric.detail}</span>
      </div>
    </article>
  );
}

function ChartCard({ title, subtitle, children, testId }) {
  return (
    <section className="chart-card" data-testid={testId}>
      <div className="section-heading">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        <button type="button" data-testid={`${testId}-details-button`} className="subtle-link">
          View details <ChevronRight size={15} />
        </button>
      </div>
      {children}
    </section>
  );
}

export default function ClientDashboard({ dashboard, user }) {
  const navigate = useNavigate();
  const metricValue = (label) => dashboard.metrics.find((item) => item.label === label)?.value || 0;
  const clientMetrics = [
    {
      label: "Open Requests",
      value: metricValue("Open Positions"),
      detail: "Current staffing and service needs",
      tone: "teal",
    },
    {
      label: "Active Providers",
      value: metricValue("Filled Positions"),
      detail: "TAG professionals serving your organization",
      tone: "green",
    },
    {
      label: "Evaluations",
      value: metricValue("Active Evaluations"),
      detail: "Evaluation requests in progress",
      tone: "gold",
    },
  ];
  return (
    <div className="app-shell" data-testid="client-dashboard-shell">
      <AppSidebar activeItem="Dashboard" />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page client-dashboard-page" data-testid="client-dashboard">
          <section className="page-intro client-welcome">
            <div>
              <p className="eyebrow">YOUR TAG SERVICES</p>
              <h1>Welcome back, {user.first_name}</h1>
              <p className="page-subtitle">Here’s what’s happening with your TAG services.</p>
            </div>
            <div className="client-welcome-actions">
              <button
                className="button button-primary"
                data-testid="client-new-staffing-request"
                type="button"
                onClick={() => navigate("/staffing-requests")}
              >
                <Plus size={17} /> New staffing request
              </button>
              <button
                className="button button-secondary"
                data-testid="client-request-evaluation"
                type="button"
                onClick={() => navigate("/client-evaluation-request")}
              >
                Request evaluation
              </button>
            </div>
          </section>
          <section className="metric-grid client-metric-grid">
            {clientMetrics.map((metric) => (
              <MetricCard key={metric.label} metric={metric} />
            ))}
          </section>
          <section className="client-attention-card" data-testid="client-needs-attention">
            <div className="section-heading">
              <div>
                <h2>Needs your attention</h2>
                <p>Actions TAG needs from your organization.</p>
              </div>
            </div>
            <div className="client-all-clear">
              <ShieldAlert size={19} />
              <div>
                <strong>You’re all caught up</strong>
                <span>No items currently require your attention.</span>
              </div>
            </div>
          </section>
          <section className="dashboard-lower-grid">
            <ChartCard
              title="Your Staffing Requests"
              subtitle="Current TAG recruiting and service requests"
              testId="client-requests-chart"
            >
              <div className="school-bars">
                {dashboard.status_breakdown.map((item, index) => (
                  <div className="school-bar" key={item.label}>
                    <div>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                    <div className="progress-track">
                      <span
                        style={{
                          width: `${Math.max(item.value * 28, 14)}%`,
                          backgroundColor: chartColors[index],
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </ChartCard>
            <section className="feed-card" data-testid="client-recent-activity">
              <div className="section-heading">
                <div>
                  <h2>Recent activity</h2>
                  <p>Updates about your TAG services</p>
                </div>
              </div>
              <div className="activity-list">
                {dashboard.recent_activity.slice(0, 4).map((activity) => (
                  <div className="activity-item" key={activity.id}>
                    <span className="activity-icon">
                      <Clock3 size={16} />
                    </span>
                    <div>
                      <strong>{humanizeActivity(activity.title)}</strong>
                      <p>{humanizeActivity(activity.detail)}</p>
                      <small>{new Date(activity.occurred_at).toLocaleDateString()}</small>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </section>
        </main>
      </div>
    </div>
  );
}

function humanizeActivity(value) {
  const text = String(value || "TAG service update");
  if (text.toLowerCase().includes("internal data administration")) {
    return "Your staffing request was updated.";
  }
  return text.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}
